import type { LevelCatalog, SaveRecord } from "@highmarch/app";
import type { BalanceData, LevelModifiers, LevelSim, LiveEventData, StarMask } from "@highmarch/domain";
import type { Badge } from "../ui/hub/badges";
import type { HubTab } from "../ui/hub/sheetGuard";
import type { SceneTarget } from "../ui/hub/sceneApi";
import type { GuideTopic } from "../ui/guide/topics";
import { AwayItemSource, registerAwayItems } from "../ui/hub/awayCard";

/**
 * The feature registry (FINISH_PLAN WP0): how a feature plugs into the hub, the More sheet, the level's end screen,
 * Settings and the Guide without editing main.ts, hubView.ts, moreSheet.ts, settings.ts or screens.ts. A feature is
 * one module under `features/` exporting a `Feature`; `features/index.ts` lists them and the composition root
 * (main.ts) installs them once, before the first screen. What a feature keeps in the save is its own save section
 * (@highmarch/app `registerSaveSection`), not a registry hook.
 *
 * Every hook is presentation glue: the rules behind a row, a pill or a badge live in domain/app like any other rule.
 */

/** What a feature gets when it registers: the game's state and the one way to store it. */
export interface FeatureContext {
  readonly balance: BalanceData;
  readonly catalog: LevelCatalog;
  readonly save: SaveRecord;
  /** The UI root (`#ui`): sheets and cards are appended here. */
  readonly ui: HTMLElement;
  /** Debug hooks are on (`?debug` or a dev build). */
  readonly debug: boolean;
  /** Clock seconds (UTC), the same clock the hub and the save use. */
  now(): number;
  /** Writes the catalog into the save and stores it, with every bound save section. */
  persist(): void;
}

/** A feature: registers its hooks once at boot. */
export interface Feature {
  /** Stable id, for logs and the order of `features/index.ts`. */
  readonly id: string;
  register(ctx: FeatureContext, registry: FeatureRegistry): void;
}

/** The hub's services while it is up, for a feature's rows, pills, pages and widgets. */
export interface HubApi {
  /** The UI root the hub's sheets mount on (the hub's single-sheet rule closes the one below a new sheet). */
  readonly parent: HTMLElement;
  /** State changed: persists the save and re-renders the hub (pills, badges, the battle card). */
  changed(): void;
  /** Opens a tab's page as a tap on the tab would. */
  openTab(tab: HubTab): void;
  /** Starts (or continues) a level the way PLAY does. */
  play(level: number): void;
  /** Where a hub-scene target stands on screen (CSS px), or null when the scene cannot say. */
  anchor(target: SceneTarget): { x: number; y: number } | null;
  /** Opens a tower's (or the Fortress's) upgrade sheet, as a tap on its Towers card would (M35: Shop → Gold). */
  openUpgrade?(towerIndex: number): void;
  /** Opens a pet's sheet (Pets v2: its level, upgrade, abilities and coats), as a tap on the pup or its Towers card would. */
  openPet?(petId: string): void;
  /** Opens the Armoury (M35: Shop → Essence's forge rows). */
  openArmoury?(): void;
}

/**
 * A panel on the Events sheet under the live event's banner and track (MP-21: Boss Week's tier ladder, Race Week's
 * medals, the Odyssey's maps): drawn for the live event, "" when it has nothing to show for it.
 */
export interface EventPanel {
  readonly id: string;
  readonly order: number;
  html(e: LiveEventData): string;
  /** Wires the panel's buttons once drawn: `close` takes the sheet down (before a run starts), `rerender` redraws it. */
  bind?(panel: HTMLElement, api: { close(): void; rerender(): void }): void;
}

/** A run the level flow is about to start (MP-21: an event mode's next map, tier or road), or null for the hub. */
export interface NextRun { readonly level: number; readonly modifiers: LevelModifiers }

/** The Shop tab's pages, one per currency (M35 Option 1). Valor and embers stay on the Events tab. */
export type ShopCurrencyPage = "gold" | "gems" | "marks" | "essence";

/**
 * Where a registered row lives since M35 (Kevin: "an improved interface rather than More"): `menu` — the top bar's ⚙
 * sheet (Settings, the Guide, Achievements, the Almanac, the Market, debug); `events` — the Events tab (events, Endless,
 * Free rewards); `shop` — reached through the Shop tab's currency pages (Shops, VIP, Skins), so it is not listed again.
 */
export type RowHome = "menu" | "events" | "shop";

/** Row order (the ⚙ menu and the Events tab): features slot between the core rows (Market 20, Achievements 30, Almanac 40…). */
export const MORE_ORDER = { events: 10, guide: 80, settings: 90, debug: 100 } as const;

/**
 * A row a feature lists (the More sheet until M35; now the ⚙ menu or the Events tab, by `home`). Its `id` is the row's
 * class (`.mrow.<id>`); a sheet it opens should carry the same class.
 */
export interface MoreRow {
  readonly id: string;
  readonly order: number;
  /** Where it is listed (default `menu`). */
  readonly home?: RowHome;
  /** A painted icon name (ui/featureIcons.ts) or art file ("icons/shop_merchant.png"); a kit glyph for debug rows only. */
  readonly icon: string;
  label(): string;
  /** The grey line under the label ("Soon", "Live · Ashen Night"). */
  note?(): string;
  /** A red count on the row (things to act on now); its home's badge (the Events tab, the ⚙ button) sums the visible rows'. */
  dot?(): number;
  visible?(): boolean;
  open(hub: HubApi): void;
}

/** A pill in the hub's top bar, after the three resource pills (gems, Marks…). */
export interface TopBarPill {
  readonly id: string;
  readonly order: number;
  /** The pill's markup (ui/kit.ts pillHtml); rendered on every hub render. */
  html(): string;
  /** `e`: the tap itself (the gems pill's "+" goes straight to the Shop). */
  onTap?(hub: HubApi, pill: HTMLElement, e?: Event): void;
}

/** A red count added to a tab's badge (a free crate on Chests, say): merged by `mergeBadge`. */
export interface TabBadge {
  readonly tab: Exclude<HubTab, "battle">;
  count(): number;
}

/** Replaces the page a tab opens. The last registered wins. */
export interface HubTabPage {
  readonly tab: Exclude<HubTab, "battle">;
  open(hub: HubApi): void;
}

/** Something the hub shows over its scene (the Tribute chip on the fortress). Mounted on every hub render. */
export interface HubWidget {
  readonly id: string;
  /** `layer` is the hub's chip layer over the scene; the handle lives until the next render or the hub goes. */
  mount(hub: HubApi, layer: HTMLElement): HubWidgetHandle;
}
export interface HubWidgetHandle {
  /** Every hub tick (1 s). */
  update?(): void;
  /** The scene was framed again (render, resize): re-anchor. */
  layout?(): void;
  dispose(): void;
}

/** What the level's end screen knows, for an extra block on it. */
export interface EndScreenFacts {
  readonly level: number;
  readonly won: boolean;
  /** The stars this run met (0 on a loss). */
  readonly stars: StarMask;
  readonly sim: LevelSim;
}

/**
 * A block on the level's end screen, between the chest and the choices (event currency earned, damage per tower).
 * Never an offer: nothing is sold on the end screen, and never after a loss (ENGAGEMENT C).
 */
export interface EndScreenExtra {
  readonly id: string;
  readonly order: number;
  /** The block's markup, or "" for none on this run. */
  html(facts: EndScreenFacts): string;
  bind?(block: HTMLElement, facts: EndScreenFacts): void;
}

/**
 * A battle as it ended, read once when the level is left (before its result is recorded): the lifetime stats' source
 * (WP7). `counts` are the battle's Daily Orders keys (domain BattleTally: kills per enemy, `kills:air`, elites).
 */
export interface BattleEnd {
  readonly sim: LevelSim;
  readonly counts: Readonly<Record<string, number>>;
  /** Damage per tower instance id (app LevelSession.damageByTower). */
  readonly damageByTower: ReadonlyMap<number, number>;
  /** Kills under each Hex Totem's curse (app LevelSession.assistsByTower): the totems' mastery. */
  readonly assistsByTower?: ReadonlyMap<number, number>;
  /** MP-27: the star conditions this run met (domain runStars; 0 on a loss), for the `battleWon` announcement. */
  readonly stars?: StarMask;
}

/** A row on the Settings sheet, after the core rows (Credits & legal, Copy diagnostics, Restore purchases…). */
export interface SettingsRow {
  readonly id: string;
  readonly order: number;
  /** `menu`: from the title and the hub only; `everywhere`: in a level too. */
  readonly scope: "menu" | "everywhere";
  title(): string;
  sub(): string;
  /** The control's markup on the right (a kit button or toggle). */
  control(): string;
  bind(row: HTMLElement, api: SettingsRowApi): void;
}
export interface SettingsRowApi {
  readonly parent: HTMLElement;
  /** Draws the Settings sheet again (a toggle changed). */
  rerender(): void;
  /** Closes the Settings sheet (a row that opens its own sheet). */
  close(): void;
}

/** Red counts add up; a word badge (NEW, !) or a count stays as it is; an info badge gives way to the red count. */
export function mergeBadge(core: Badge | null, extra: number): Badge | null {
  if (extra <= 0) return core;
  if (core === null || core.tone === "info") return { tone: "alert", text: String(extra) };
  if (core.glyph) return core;
  const n = Number(core.text);
  return Number.isInteger(n) ? { tone: "alert", text: String(n + extra) } : core;
}

/**
 * MP-06/MP-25: where a row on a pre-battle surface is drawn — the hub's battle card (the next level; `farmLevel` the
 * level its FARM button replays, or null) or the campaign map's level panel (the selected level).
 */
export interface PreBattleContext { readonly surface: "card" | "map"; readonly level: number; readonly cleared: boolean; readonly farmLevel: number | null }
/** A row a feature adds to the pre-battle surfaces (supplies, a farm twist). Its taps are the feature's (delegated). */
export interface PreBattleRow { readonly id: string; readonly order: number; html(ctx: PreBattleContext): string }

const byOrder = <T extends { readonly order: number }>(list: readonly T[]): T[] => [...list].sort((a, b) => a.order - b.order);

/** Everything the features registered. One instance for the game (`features`); tests make their own. */
export class FeatureRegistry {
  private readonly rows: MoreRow[] = [];
  private readonly farmExtras: PreBattleRow[] = [];
  private readonly pills: TopBarPill[] = [];
  private readonly badges: TabBadge[] = [];
  private readonly pages = new Map<HubTabPage["tab"], HubTabPage>();
  private readonly widgets: HubWidget[] = [];
  private readonly extras: EndScreenExtra[] = [];
  private readonly settings: SettingsRow[] = [];
  private readonly topics: GuideTopic[] = [];
  private readonly battleListeners: ((b: BattleEnd) => void)[] = [];
  private readonly panels: EventPanel[] = [];
  private readonly runListeners: ((level: number, modifiers: LevelModifiers) => void)[] = [];
  private readonly preBattle: PreBattleRow[] = [];
  private readonly persistListeners: (() => void)[] = [];

  /**
   * Starts a level with run modifiers (a Heroic replay, the Weekly Trial; WP6) the way PLAY starts one: the level flow
   * sets it at boot (boot/levelFlow.ts LevelFlow.requestStart). A no-op until then (tests).
   */
  startRun: (level: number, modifiers: LevelModifiers) => void = () => undefined;
  /**
   * Opens a level's Iron challenge sheet (M21, docs/MODES.md) from the campaign map or the battle card: the modes
   * feature sets it at boot (features/modes.ts). A no-op until then (tests).
   */
  openIron: (level: number, parent: HTMLElement) => void = () => undefined;
  /** Gear v2 (features/forge.ts): opens the Free rewards sheet (the milestone tracks); a no-op until it registers. */
  openFreeRewards: (parent: HTMLElement) => void = () => undefined;
  /** Gear v2: how many free-route milestones wait to be claimed (the campaign map's Iron chip, the More badge). */
  freeRewardsWaiting: () => { readonly iron: number; readonly endless: number; readonly ironStars: number; readonly ironOpen: boolean } = () => ({ iron: 0, endless: 0, ironStars: 0, ironOpen: false });
  /**
   * M35: opens the Shop tab on a currency's page (the Shop tab, a top-bar currency pill): the economy feature sets it
   * at boot (features/economy.ts). A no-op until then (tests).
   */
  openShop: (hub: HubApi, page: ShopCurrencyPage) => void = () => undefined;
  /**
   * MP-21: the running battle's tally so far (domain BattleTally keys: kills per enemy, elites, `wavesCalled`), read by
   * the end screen's event block before the level is left: boot/metaFlow.ts sets it while a battle runs. Empty until then (tests).
   */
  battleCounts: () => Readonly<Record<string, number>> = () => ({});
  /**
   * MP-21: the run an event mode's end-screen button starts after a run with `modifiers` (`won` its result) — Boss Week's
   * next tier, Race Week's road again, the Odyssey's next map or attempt — or null for the hub. Set by features/eventModes.ts.
   */
  nextEventRun: (modifiers: LevelModifiers, won: boolean) => NextRun | null = () => null;
  /**
   * MP-06/MP-25: a fresh battle's run modifiers as it starts (the level flow calls it once per new attempt, never for a
   * resumed one): the supplies feature takes the armed supplies and a farm twist into them, and the pets feature
   * (registered after it) wraps that to add the pet. The identity until set.
   */
  prepareRun: (level: number, modifiers: LevelModifiers | null) => LevelModifiers | null = (_level, modifiers) => modifiers;

  moreRow(row: MoreRow): void { this.rows.push(row); }
  topBarPill(pill: TopBarPill): void { this.pills.push(pill); }
  tabBadge(badge: TabBadge): void { this.badges.push(badge); }
  hubTabPage(page: HubTabPage): void { this.pages.set(page.tab, page); }
  hubWidget(widget: HubWidget): void { this.widgets.push(widget); }
  /** Lines on the "while you were away" card (ui/hub/awayCard.ts); returns the unregister. */
  awayItems(source: AwayItemSource): () => void { return registerAwayItems(source); }
  endScreenExtra(extra: EndScreenExtra): void { this.extras.push(extra); }
  settingsRow(row: SettingsRow): void { this.settings.push(row); }
  /** A row on the pre-battle surfaces (MP-06: the battle card and the campaign map's panel). */
  preBattleRow(row: PreBattleRow): void { this.preBattle.push(row); }
  /** Something a feature puts on the battle card's FARM row, after the payout (owner 2026-10-23: the farm twist). */
  farmRowExtra(row: PreBattleRow): void { this.farmExtras.push(row); }
  farmRowHtml(ctx: PreBattleContext): string { return byOrder(this.farmExtras).map((r) => r.html(ctx)).join(""); }
  /** Every pre-battle row's markup for `ctx`, in order ("" when none shows). */
  preBattleHtml(ctx: PreBattleContext): string { return byOrder(this.preBattle).map((r) => r.html(ctx)).join(""); }
  /** Called once per battle as its level is left (boot/metaFlow.ts recordBattleOrders). */
  battleEnd(listener: (b: BattleEnd) => void): void { this.battleListeners.push(listener); }
  /** Called after every save the game makes (boot/metaFlow.ts persist): the reward flight compares holdings here. */
  afterPersist(listener: () => void): void { this.persistListeners.push(listener); }
  /** The save was just stored (boot/metaFlow.ts calls it once per persist). */
  persisted(): void { for (const l of this.persistListeners) l(); }
  /** The level flow reports a finished (or abandoned) battle to every listener. */
  battleEnded(b: BattleEnd): void { for (const l of this.battleListeners) l(b); }
  /** A Guide topic (More → Guide), replayable like the core ones; its id must be a GuideTopicId. */
  /** MP-21: a panel on the Events sheet (eventPanels() lists them by order). */
  eventPanel(panel: EventPanel): void { this.panels.push(panel); }
  eventPanels(): readonly EventPanel[] { return byOrder(this.panels); }
  /** MP-21: called when a fresh run with modifiers is built (never a resumed one): an Odyssey map begins its attempt. */
  runStarted(listener: (level: number, modifiers: LevelModifiers) => void): void { this.runListeners.push(listener); }
  /** The level flow reports a fresh run with modifiers to every listener. */
  runBegan(level: number, modifiers: LevelModifiers): void { for (const l of this.runListeners) l(level, modifiers); }
  guideTopic(topic: GuideTopic): void {
    if (this.topics.some((t) => t.id === topic.id)) throw new Error(`guide topic "${topic.id}" is already registered`);
    this.topics.push(topic);
  }

  moreRows(): readonly MoreRow[] { return byOrder(this.rows); }
  topBarPills(): readonly TopBarPill[] { return byOrder(this.pills); }
  /** The extra red count features add to `tab`'s badge. */
  tabBadgeCount(tab: TabBadge["tab"]): number { return this.badges.filter((b) => b.tab === tab).reduce((n, b) => n + Math.max(0, b.count()), 0); }
  hubTabPageFor(tab: HubTab): HubTabPage | undefined { return tab === "battle" ? undefined : this.pages.get(tab); }
  hubWidgets(): readonly HubWidget[] { return this.widgets; }
  endScreenExtras(): readonly EndScreenExtra[] { return byOrder(this.extras); }
  settingsRows(inLevel: boolean): readonly SettingsRow[] { return byOrder(this.settings.filter((r) => !inLevel || r.scope === "everywhere")); }
  guideTopics(): readonly GuideTopic[] { return this.topics; }
}

/** The game's registry. */
export const features = new FeatureRegistry();

/** Registers every feature once (main.ts, at boot). */
export function installFeatures(list: readonly Feature[], ctx: FeatureContext, registry: FeatureRegistry = features): void {
  for (const f of list) f.register(ctx, registry);
}
